import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import { Icon } from '#/components/Icon/Icon';
import { useI18n } from '#/i18n/hooks/useI18n';
import {
  LayoutHeaderPanelLink,
  LayoutHeaderPanelSectionTitle,
} from '#/layouts/Layout/Header/Panel/LayoutHeaderPanel.styled';
import { LayoutHeaderPanelRowContent } from '#/layouts/Layout/Header/Panel/LayoutHeaderPanelRowContent';
import {
  orgDashboardManageListingsPath,
  orgDashboardRootPath,
} from '#/modules/orgDashboard/routing/orgDashboard.paths';
import { useRouteParams } from '#/routing/hooks/useRouteParams';

import type { HamburgerMenuExpandableRowType } from './data/types/HamburgerMenuData';
import {
  HamburgerMenuOrganizationCard,
  HamburgerMenuOrgLink,
  HamburgerMenuRowChevronHolder,
  HamburgerMenuRowsHolder,
} from './HamburgerMenu.styled';
import {
  hamburgerMenuConstantsByRowType,
  hamburgerMenuTrackingName,
} from './hamburgerMenuConstants';
import { HamburgerMenuExpandableRow } from './HamburgerMenuExpandableRow';

type Props = {
  user: ApiUser;
  title: string;
  expandedRowTypes: ReadonlySet<HamburgerMenuExpandableRowType>;
  onToggleExpanded: (type: HamburgerMenuExpandableRowType) => void;
};

const trackingContext = `${hamburgerMenuTrackingName} > Organization`;

// Same event as the Workspace dropdown, which this replaces on mobile
const orgClickActions = {
  closeRichDetailsFromWithin: true,
  track: {
    name: 'Updated Workspace - Org',
    properties: { context: trackingContext },
  },
};

export function HamburgerMenuOrganizationSection({
  user,
  title,
  expandedRowTypes,
  onToggleExpanded,
}: Props) {
  const { i18n } = useI18n();
  const { orgId: currentOrgId } = useRouteParams(orgDashboardRootPath);

  const rowTitle = i18n('Organization Dashboard');
  const [firstOrg] = user.orgs;
  // On a dashboard the current org goes first, so you don't have to scroll to
  // find where you are. The rest keep the user.orgs order.
  const orderedOrgs = [
    ...user.orgs.filter((org) => org.id === currentOrgId),
    ...user.orgs.filter((org) => org.id !== currentOrgId),
  ];

  return (
    <HamburgerMenuOrganizationCard data-qa-id="hamburger-menu-section-organization">
      <LayoutHeaderPanelSectionTitle>{title}</LayoutHeaderPanelSectionTitle>

      <HamburgerMenuRowsHolder>
        {user.orgs.length === 1 ? (
          <li>
            <LayoutHeaderPanelLink
              type="link"
              href={orgDashboardManageListingsPath.with({ orgId: firstOrg.id })}
              qaId="hamburger-menu-organization-dashboard-link"
              clickActions={orgClickActions}
            >
              <LayoutHeaderPanelRowContent
                icon={hamburgerMenuConstantsByRowType.organizations.icon}
                title={rowTitle}
              />
              <HamburgerMenuRowChevronHolder aria-hidden>
                <Icon name="arrow-right" size={12} />
              </HamburgerMenuRowChevronHolder>
            </LayoutHeaderPanelLink>
          </li>
        ) : (
          <HamburgerMenuExpandableRow
            type="organizations"
            title={rowTitle}
            trackingContext={hamburgerMenuTrackingName}
            expanded={expandedRowTypes.has('organizations')}
            onToggleExpanded={onToggleExpanded}
            // The check goes in the icon slot, so org names line up with the row title
            nested={false}
          >
            {orderedOrgs.map((org) => {
              const isCurrent = org.id === currentOrgId;

              return (
                <li key={org.id}>
                  <HamburgerMenuOrgLink
                    type="link"
                    href={orgDashboardManageListingsPath.with({
                      orgId: org.id,
                    })}
                    qaId="hamburger-menu-organizations-org-link"
                    aria-current={isCurrent || undefined}
                    clickActions={orgClickActions}
                  >
                    <LayoutHeaderPanelRowContent
                      icon={
                        isCurrent
                          ? { kind: 'icon', name: 'simple-check' }
                          : { kind: 'none' }
                      }
                      title={org.name}
                    />
                  </HamburgerMenuOrgLink>
                </li>
              );
            })}
          </HamburgerMenuExpandableRow>
        )}
      </HamburgerMenuRowsHolder>
    </HamburgerMenuOrganizationCard>
  );
}
